import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { orderDeliveryService } from '../../services/orderDelivery.service';
import { formatCustomNumber } from '../../helpers/formatCustomNumber';
import { GetOrderDeliveryList, SetOrderDeliveryList } from '../../store/orderDeliveryAtom';
import { taskStatusClass } from './orderDeliveryStatus';
import OrderDeliveryScroll from './OrderDeliveryScroll';

const formatDate = value => (value ? moment(value).format('DD-MM-YYYY HH:mm') : null);
const naira = value => `₦${formatCustomNumber(Number(value) || 0)}`;

const timelineSteps = [
  { label: 'Assigned', key: 'task_assigned_date' },
  { label: 'Accepted', key: 'task_accepted_date' },
  { label: 'Picked Up', key: 'task_pickup_date' },
  { label: 'Out For Delivery', key: 'task_delivery_out_date' },
  { label: 'Arrived', key: 'task_arrived_date' },
  { label: 'Delivered', key: 'task_delivered_date' },
  { label: 'Cancelled / Failed', key: 'task_cancelled_date' },
];

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-1 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="text-right font-medium text-gray-800">{value || '-'}</span>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <section className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-gray-500">{title}</h3>
      {children}
    </section>
  );
}

function OrderDeliveryDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const url = pathname.split('/');
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3];

  const getOrderDelivery = GetOrderDeliveryList();
  const setOrderDelivery = SetOrderDeliveryList();
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    if (!getOrderDelivery.task_list?.length) {
      orderDeliveryService.getOrderDeliveryList().then(res => {
        if (res?.data && isMounted) {
          setOrderDelivery(res.data);
        }
      });
    }
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.title = 'Order Delivery Details';
    setLoading(true);
    orderDeliveryService
      .getOrderDeliveryDetails(id)
      .then(res => {
        setOrder(res?.data?.Sales_Order_info?.[0] ?? null);
        setItems(res?.data?.Sales_Order_details ?? []);
      })
      .finally(() => setLoading(false));
  }, [id]);

  function viewItemDetails(refNo) {
    navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${refNo}`);
  }

  const itemsSubtotal = items.reduce((sum, item) => sum + (Number(item?.total_amount) || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-row items-center justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-bold">{order?.sales_order_no ?? `Order #${id}`}</h2>
              {order?.task_status_name && (
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    taskStatusClass[order.task_status_id] ?? 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {order.task_status_name}
                </span>
              )}
            </div>
            {order && (
              <p className="text-xs text-gray-500">
                Order #{order.sales_order_ref_no} · {order.sales_order_date_string} · {order.sales_order_status_name}
              </p>
            )}
          </div>
          <button onClick={() => navigate(-1)} className="btn btn-light">
            Back
          </button>
        </div>

        <div className="flex flex-col gap-6 lg:flex-row">
          <div className="w-full flex-shrink-0 lg:w-64">
            <OrderDeliveryScroll viewItemDetails={viewItemDetails} />
          </div>

          <div className="min-w-0 flex-1 space-y-4">
            {loading && !order ? (
              <p className="text-gray-500">Loading order...</p>
            ) : !order ? (
              <p className="text-gray-500">No delivery record found for this order.</p>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Card title="Recipient">
                    <InfoRow label="Name" value={order.order_recipient_name?.trim()} />
                    <InfoRow label="Phone" value={order.order_recipient_phone_number} />
                    <InfoRow label="Email" value={order.order_recipient_email} />
                    <InfoRow label="Address" value={order.order_recipient_full_shipping_address} />
                    <InfoRow label="Gift" value={order.gift_status_name} />
                  </Card>

                  <Card title="Customer">
                    <InfoRow label="Name" value={order.customer_full_name} />
                    <InfoRow label="Customer ID" value={order.customer_id} />
                    <InfoRow label="Mobile" value={order.customer_mobile_no} />
                    <InfoRow label="Email" value={order.customer_email_address} />
                    <InfoRow label="Subscription" value={order.subscription_ref_no} />
                  </Card>

                  <Card title="Delivery Task">
                    <InfoRow label="Status" value={order.task_status_name} />
                    <InfoRow label="Driver" value={order.delivery_user_full_name} />
                    <InfoRow label="Delivery method" value={order.delivery_method_name} />
                    <InfoRow label="Verification code" value={order.delivery_verification_code} />
                    {order.task_reason_name && <InfoRow label="Reason" value={order.task_reason_name} />}
                    {order.task_cancellation_remark && <InfoRow label="Remark" value={order.task_cancellation_remark} />}
                    {order.delivery_image_url && (
                      <a href={order.delivery_image_url} target="_blank" rel="noreferrer" className="mt-2 block text-sm text-blue-600 underline">
                        View delivery photo
                      </a>
                    )}
                  </Card>

                  <Card title="Warehouse">
                    <InfoRow label="Name" value={order.warehouse_name} />
                    <InfoRow label="Contact" value={order.warehouse_contact_person} />
                    <InfoRow label="Phone" value={order.warehouse_contact_number} />
                    <InfoRow label="Address" value={order.warehouse_address} />
                  </Card>
                </div>

                <Card title="Task Timeline">
                  <ol className="relative ml-2 border-l border-gray-200">
                    {timelineSteps.map(step => {
                      const date = formatDate(order[step.key]);
                      return (
                        <li key={step.key} className="mb-3 ml-4 last:mb-0">
                          <span
                            className={`absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border border-white ${
                              date ? (step.key === 'task_cancelled_date' ? 'bg-red-500' : 'bg-green-500') : 'bg-gray-300'
                            }`}
                          />
                          <div className="flex justify-between text-sm">
                            <span className={date ? 'font-medium text-gray-800' : 'text-gray-400'}>{step.label}</span>
                            <span className="text-gray-500">{date ?? '-'}</span>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </Card>

                <Card title={`Items (${items.length})`}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs uppercase text-gray-500">
                          <th className="py-2 pr-2">Item</th>
                          <th className="py-2 pr-2">Weight</th>
                          <th className="py-2 pr-2 text-right">Qty</th>
                          <th className="py-2 pr-2 text-right">Rate</th>
                          <th className="py-2 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map(item => (
                          <tr key={`${item?.item_id}-${item?.zoho_item_id}`} className="border-b last:border-0">
                            <td className="py-2 pr-2">
                              <div className="flex items-center gap-3">
                                {item?.image_url ? (
                                  <img src={item.image_url} alt="" className="h-10 w-10 rounded object-cover" />
                                ) : (
                                  <div className="h-10 w-10 rounded bg-gray-100" />
                                )}
                                <div className="min-w-0">
                                  <div className="font-medium text-gray-800">{item?.item_name}</div>
                                  <div className="text-xs text-gray-400">{item?.item_sku}</div>
                                </div>
                              </div>
                            </td>
                            <td className="py-2 pr-2 text-gray-600">{item?.sales_order_item_weight_description || '-'}</td>
                            <td className="py-2 pr-2 text-right">{item?.item_sales_quantity}</td>
                            <td className="py-2 pr-2 text-right">{naira(item?.item_sales_rate)}</td>
                            <td className="py-2 text-right font-semibold">{naira(item?.total_amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="ml-auto mt-4 max-w-xs border-t pt-3">
                    <InfoRow label="Items subtotal" value={naira(itemsSubtotal)} />
                    <InfoRow label={`Discount (${order.discount_type_name ?? '-'})`} value={naira(order.discount_amount)} />
                    <InfoRow label="Delivery charge" value={naira(order.delivery_charge_amount)} />
                    <InfoRow label="Adjustment" value={naira(order.adjustment_amount)} />
                    <div className="mt-1 flex justify-between border-t pt-2 text-base font-bold">
                      <span>Total</span>
                      <span>{naira(order.total_sales_order_amount)}</span>
                    </div>
                  </div>
                </Card>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderDeliveryDetailsPage;
